import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Form, Input, InputNumber, Modal, Row, Statistic, Typography, message, Table } from "antd";
import { axiosInstance } from "../../api/axios";

const { Title } = Typography;

type ShiftDto = {
    id: number;
    openedAtUtc: string;
    closedAtUtc: string | null;
    openingCash: number;
    closingCashCounted: number | null;
    status: string;
    notes: string | null;
};

type ShiftSummaryDto = {
    shiftId: number;
    userId: string;
    openedAtUtc: string;
    closedAtUtc: string | null;
    openingCash: number;
    cashPaymentsTotal: number;
    expectedCashInDrawer: number;
    closingCashCounted: number | null;
    cashVariance: number | null;
    currencies: Array<{
        currencyCode: string;
        totalSales: number;
        saleCount: number;
        paymentsByMethod: Array<{ paymentMethod: string; amount: number }>;
    }>;
};

export const ShiftCloseoutReport: React.FC = () => {
    const [refreshKey, setRefreshKey] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [closeModal, setCloseModal] = useState(false);
    const [openForm] = Form.useForm();
    const [closeForm] = Form.useForm();

    const [currentShift, setCurrentShift] = useState<ShiftDto | null>(null);
    const [summary, setSummary] = useState<ShiftSummaryDto | null>(null);
    const [loadingCurrent, setLoadingCurrent] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(false);

    const reload = useCallback(() => setRefreshKey((k) => k + 1), []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoadingCurrent(true);
            try {
                const { data } = await axiosInstance.get<{ data: ShiftDto }>("cashier-shifts/current");
                if (!cancelled) setCurrentShift(data.data ?? null);
            } catch {
                if (!cancelled) setCurrentShift(null);
            } finally {
                if (!cancelled) setLoadingCurrent(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    useEffect(() => {
        if (!currentShift?.id) {
            setSummary(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoadingSummary(true);
            try {
                const { data } = await axiosInstance.get<{ data: ShiftSummaryDto }>(
                    `cashier-shifts/${currentShift.id}/summary`
                );
                if (!cancelled) setSummary(data.data ?? null);
            } catch {
                if (!cancelled) setSummary(null);
            } finally {
                if (!cancelled) setLoadingSummary(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [currentShift?.id, refreshKey]);

    const currencyRows = useMemo(() => summary?.currencies ?? [], [summary]);

    return (
        <div>
            <Title level={3}>Shift Close-out (End of Day)</Title>

            <Card style={{ marginBottom: 16 }} loading={loadingCurrent}>
                {!currentShift ? (
                    <Row justify="space-between" align="middle">
                        <Col>
                            <div>No open shift.</div>
                        </Col>
                        <Col>
                            <Button type="primary" onClick={() => setOpenModal(true)}>
                                Open Shift
                            </Button>
                        </Col>
                    </Row>
                ) : (
                    <Row gutter={16} align="middle" justify="space-between">
                        <Col>
                            <div>
                                <strong>Status:</strong> {currentShift.status} | <strong>Opened:</strong>{" "}
                                {new Date(currentShift.openedAtUtc).toLocaleString()}
                            </div>
                            <div>
                                <strong>Opening cash:</strong> {Number(currentShift.openingCash).toFixed(2)}
                            </div>
                        </Col>
                        <Col>
                            <Button onClick={() => setOpenModal(true)} disabled>
                                Open Shift
                            </Button>{" "}
                            <Button type="primary" danger onClick={() => setCloseModal(true)}>
                                Close Shift
                            </Button>
                        </Col>
                    </Row>
                )}
            </Card>

            {currentShift && (
                <Card title="Shift summary" loading={loadingSummary}>
                    {summary ? (
                        <>
                            <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col span={6}>
                                    <Statistic title="Cash payments total" value={summary.cashPaymentsTotal} precision={2} />
                                </Col>
                                <Col span={6}>
                                    <Statistic title="Expected cash in drawer" value={summary.expectedCashInDrawer} precision={2} />
                                </Col>
                                <Col span={6}>
                                    <Statistic
                                        title="Closing cash counted"
                                        value={summary.closingCashCounted ?? 0}
                                        precision={2}
                                    />
                                </Col>
                                <Col span={6}>
                                    <Statistic title="Cash variance" value={summary.cashVariance ?? 0} precision={2} />
                                </Col>
                            </Row>

                            <Table
                                dataSource={currencyRows}
                                rowKey={(r) => r.currencyCode}
                                pagination={false}
                                columns={[
                                    { title: "Currency", dataIndex: "currencyCode", key: "currencyCode" },
                                    { title: "Transactions", dataIndex: "saleCount", key: "saleCount" },
                                    {
                                        title: "Total sales",
                                        dataIndex: "totalSales",
                                        key: "totalSales",
                                        render: (v) => Number(v).toFixed(2),
                                    },
                                    {
                                        title: "Payments",
                                        key: "payments",
                                        render: (_, r) =>
                                            r.paymentsByMethod
                                                .map((p) => `${p.paymentMethod}: ${Number(p.amount).toFixed(2)}`)
                                                .join(" | "),
                                    },
                                ]}
                            />
                        </>
                    ) : (
                        <div>No summary available.</div>
                    )}
                </Card>
            )}

            <Modal
                title="Open shift"
                open={openModal}
                onCancel={() => setOpenModal(false)}
                okText="Open"
                onOk={async () => {
                    try {
                        const values = await openForm.validateFields();
                        const branchRaw = values.branchId;
                        const branchId =
                            branchRaw === undefined || branchRaw === null || branchRaw === ""
                                ? null
                                : Number(branchRaw);
                        await axiosInstance.post("cashier-shifts/open", {
                            openingCash: values.openingCash,
                            branchId: Number.isFinite(branchId as number) ? branchId : null,
                        });
                        message.success("Shift opened.");
                        setOpenModal(false);
                        openForm.resetFields();
                        reload();
                    } catch (e: any) {
                        const msg = e?.response?.data ?? e?.message ?? "Failed to open shift.";
                        message.error(typeof msg === "string" ? msg : "Failed to open shift.");
                    }
                }}
            >
                <Form form={openForm} layout="vertical">
                    <Form.Item label="Opening cash" name="openingCash" rules={[{ required: true }]}>
                        <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
                    </Form.Item>
                    <Form.Item label="Branch id (optional)" name="branchId">
                        <InputNumber style={{ width: "100%" }} min={1} step={1} />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Close shift"
                open={closeModal}
                onCancel={() => setCloseModal(false)}
                okText="Close"
                okButtonProps={{ danger: true, type: "primary" }}
                onOk={async () => {
                    try {
                        const values = await closeForm.validateFields();
                        await axiosInstance.post("cashier-shifts/close", {
                            closingCashCounted: values.closingCashCounted ?? null,
                            notes: values.notes ?? null,
                        });
                        message.success("Shift closed.");
                        setCloseModal(false);
                        closeForm.resetFields();
                        reload();
                    } catch (e: any) {
                        const msg = e?.response?.data ?? e?.message ?? "Failed to close shift.";
                        message.error(typeof msg === "string" ? msg : "Failed to close shift.");
                    }
                }}
            >
                <Form form={closeForm} layout="vertical">
                    <Form.Item label="Closing cash counted" name="closingCashCounted">
                        <InputNumber style={{ width: "100%" }} min={0} step={0.01} />
                    </Form.Item>
                    <Form.Item label="Notes" name="notes">
                        <Input.TextArea rows={3} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
